import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { portalApi, type PortalProfile } from '@/api/portal';

export default function MerchantKeysPage() {
  const [profile, setProfile] = useState<PortalProfile | null>(null);
  const [appSecret, setAppSecret] = useState('');
  const [copied, setCopied] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    portalApi.profile().then(setProfile).catch((err) => setError(err instanceof Error ? err.message : '加载失败'));
  }, []);

  const copy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const saveApp = async () => {
    if (!profile) return;
    setError('');
    setMessage('');
    try {
      await portalApi.updateApp({ appName: profile.appName, ipWhitelist: profile.ipWhitelist });
      setMessage('应用配置已保存');
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    }
  };

  const revealSecret = async () => {
    if (!profile) return;
    setError('');
    setMessage('');
    try {
      const res = await portalApi.revealAppSecret();
      setAppSecret(res.appSecret);
      setProfile({ ...profile, secretViewAvailable: false });
      setMessage('AppSecret 已显示，本次关闭页面后将无法再次查看，请立即保存到你的服务端。');
    } catch (err) {
      setError(err instanceof Error ? err.message : '查看失败');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-4xl">
        {message && <div className="rounded-lg bg-green-500/10 border border-green-500/20 px-4 py-3 text-sm text-green-300">{message}</div>}
        {error && <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">{error}</div>}
        {!profile ? (
          <div className="tech-card p-10 text-center text-slate-400">正在加载...</div>
        ) : (
          <>
            <div className="tech-card p-5">
              <h3 className="text-base font-semibold text-slate-100 mb-4">应用信息</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">应用名称</label>
                  <input value={profile.appName || ''} onChange={(e) => setProfile({ ...profile, appName: e.target.value })} className="tech-input w-full px-3 py-2 text-sm" />
                </div>
                <ReadonlySecret label="AppId" value={profile.appId} copied={copied === 'appId'} onCopy={() => copy(profile.appId, 'appId')} />
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">AppSecret</label>
                  <div className="flex items-center gap-3">
                    <code className="font-mono text-sm text-blue-300 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-700/60 flex-1 truncate">
                      {appSecret || '••••••••••••••••••••••••••••••••'}
                    </code>
                    {appSecret ? (
                      <button onClick={() => copy(appSecret, 'appSecret')} className="tech-btn-secondary px-3 py-2 text-xs">{copied === 'appSecret' ? '已复制' : '复制'}</button>
                    ) : (
                      <button onClick={revealSecret} disabled={!profile.secretViewAvailable} className="tech-btn-secondary px-3 py-2 text-xs disabled:opacity-50">
                        {profile.secretViewAvailable ? '查看一次' : '需管理员重置'}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">AppSecret 只能查看一次；查看后如遗失，需要联系管理员重置。</p>
                </div>
              </div>
            </div>

            <div className="tech-card p-5">
              <h3 className="text-base font-semibold text-slate-100 mb-4">IP 白名单</h3>
              <textarea
                value={profile.ipWhitelist || ''}
                onChange={(e) => setProfile({ ...profile, ipWhitelist: e.target.value })}
                rows={5}
                placeholder="每行或逗号分隔：1.2.3.4, 10.0.0.0/24；留空表示不限制"
                className="tech-input w-full px-3 py-2 text-sm font-mono"
              />
              <p className="text-xs text-slate-500 mt-2">开放网关下单、查单会校验该白名单，支持单个 IP 和 CIDR。</p>
              <button onClick={saveApp} className="tech-btn-primary mt-4 px-5 py-2 text-sm">保存应用配置</button>
            </div>

            <div className="rounded-xl border border-amber-500/15 bg-amber-500/5 p-5">
              <h4 className="text-sm font-semibold text-amber-300 mb-2">密钥安全提醒</h4>
              <p className="text-sm text-amber-400/80">AppSecret 只能保存在你的服务端，用于生成下单和查单签名，不能出现在小程序、H5 或移动端代码中。</p>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function ReadonlySecret({ label, value, copied, onCopy }: { label: string; value: string; copied: boolean; onCopy: () => void }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-1">{label}</label>
      <div className="flex items-center gap-3">
        <code className="font-mono text-sm text-blue-300 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-700/60 flex-1 truncate">{value}</code>
        <button onClick={onCopy} className="tech-btn-secondary px-3 py-2 text-xs">{copied ? '已复制' : '复制'}</button>
      </div>
    </div>
  );
}
